import React, { forwardRef } from 'react';
import styles from './SearchBar.module.css';

/**
 * SearchBar Component
 *
 * A reusable search input component for filtering data
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {string} props.value - Search value
 * @param {function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.loading - Show loading state
 * @param {function} props.onClear - Clear handler
 * @param {string} props.size - SearchBar size: 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const SearchBar = forwardRef(({
  value = '',
  onChange,
  placeholder = 'Search...',
  loading = false,
  onClear,
  size = 'md',
  className = '',
  ...rest
}, ref) => {
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  const containerClasses = [styles.container, styles[size], className]
    .filter(Boolean)
    .join(' ');

  const showClearButton = value && value.length > 0;

  return (
    <div className={containerClasses}>
      <div className={styles.iconWrapper}>
        <svg
          className={styles.searchIcon}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <input
        ref={ref}
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        {...rest}
      />

      {loading && (
        <div className={styles.loadingSpinner}>
          <svg
            className={styles.spinner}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className={styles.spinnerCircle}
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className={styles.spinnerPath}
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {!loading && showClearButton && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
          aria-label="Clear search"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
